/**
 * This module manages the memos model of the API
 * @file
 * @author Benjamin Listwon <ben@thebrandingfarm.com>
 * @author Jason Haddix <jason@thebrandingfarm.com>
 * @author DJ Kim <dj@thebrandingfarm.com>
 */ 

import debounce from 'lodash/debounce'
import isEmpty from 'lodash/isEmpty'
import cloneDeep from 'lodash/cloneDeep'

import api from '@/api'
// import router from '@/router'
import { 
    // MEMOS
    VUEX_MEMOS_GET_REQUEST, 
    VUEX_MEMOS_GET_SUCCESS,
    VUEX_MEMOS_GET_FAILURE, 
    VUEX_MEMOS_APPLY_FILTERS,
    VUEX_MEMOS_CLEAR_FILTERS,
    VUEX_MEMOS_APPLY_STATUS_FILTER,
    VUEX_MEMOS_APPLY_PROGRESS_FILTER,
    VUEX_MEMOS_APPLY_TYPE_FILTER,
    VUEX_MEMOS_APPLY_DEPARTMENT_FILTER,
    VUEX_MEMOS_APPLY_CREATIVE_ROUND_FILTER,
    VUEX_MEMOS_SYNC_PAGINATION,
    VUEX_MEMOS_SET_APPROVED,
    
    // MEMO
    VUEX_MEMO_GET_REQUEST, 
    VUEX_MEMO_GET_SUCCESS, 
    VUEX_MEMO_GET_EVENTS_SUCCESS,
    VUEX_MEMO_UPDATE_PROP,                  // TODO: Remove
    VUEX_MEMO_UPDATE_APPLY_TEMPLATE,        // TODO: Remove
    VUEX_MEMO_UPDATE_REQUEST,
    VUEX_MEMO_UPDATE_REQUEST_SUCCESS,
    VUEX_MEMO_UPDATE_REQUEST_FAILURE,
    
    // CREATE MEMO
    VUEX_MEMO_CREATE_INIT,                  // TODO: Remove
    VUEX_MEMO_CREATE, 
    VUEX_MEMO_CREATE_APPLY_TEMPLATE,        // TODO: Remove
    VUEX_MEMO_CREATE_SUCCESS,
    VUEX_MEMO_CREATE_FAILURE,
    VUEX_MEMO_CREATE_UPDATE,                // TODO: Remove
    VUEX_MEMO_CREATE_UPDATE_SUCCESS,        // TODO: Remove
    VUEX_MEMO_CREATE_UPDATE_FAILURE,        // TODO: Remove
    VUEX_MEMO_CREATE_CANCEL,
    VUEX_MEMO_CREATE_CANCEL_PROMPT,         // TODO: Remove
    VUEX_MEMO_CREATE_CLEAR_PROPS_TO_UPDATE, // TODO: Remove
    VUEX_MEMO_CREATE_UPDATE_PROP,           // TODO: Remove
    VUEX_MEMO_CREATE_HAS_CHANGED,           // TODO: Remove

    // REMOVE MEMO
    VUEX_MEMO_REMOVE,
    VUEX_MEMO_REMOVE_SUCCESS,
    VUEX_MEMO_REMOVE_FAILURE,
    
    // ATTACHMENTS
    VUEX_MEMO_ATTACH_FILE,
    VUEX_MEMO_GET_ATTACHMENTS,
    VUEX_MEMO_GET_ATTACHMENTS_SUCCESS,
    VUEX_MEMO_REMOVE_FILE,
    VUEX_MEMO_REMOVE_FILE_SUCCESS,
    
    // ROUTE EXIT
    VUEX_MEMO_PAGINATION_EXIT } from '@/store/constants/memos'

import { 
    VUEX_ROUTING_EXIT_MEMO } from '@/store/constants/routing'

import { 
    VUEX_AUTH_LOGOUT } from '@/store/constants/auth'

import {
    VUEX_ATTACHMENTS_DELETE_ATTACHMENT } from '@/store/constants/attachments'



import {
    VUEX_SNACKBAR_SHOW } from '@/store/constants/snackbar'

/**
 * An empty representation of a memo object model needed for new memo creation
 * @const
 * @todo This will become dynamically registered vuex module when the creation route loads
 */

const createMemo_Template = {
    name:              null,
    content:           null,
    created_at:        null,
    updated_at:        null,
   /*  files: [
        // FILES
    ] */
}

const updateMemo_Template = {
    name:              null,
    content:           null,
    created_at:        null,
    updated_at:        null,
    /* files: [
        // FILES
    ] */
}


/**
* Memos module state
* @const
*/
const state = {
    memos: [],
    memosLinks: {},
    memosLoaded: true,
    memosApproved: true,
    
    memo: {},
    memoLinks: {},
    memoEvents: {},
    memoEventsLinks: {},
    // memoFiles: {},
    memoAttachments: [],
    memoLoaded: false,
    restorePreviousMemoList: false,
    appliedFilters: [],
    pagination: {
        rowsPerPage: 10,
        sortBy: 'created_at',
        descending: true,
        page: 1
    },
    
    updateMemo_Data: {},
    createMemo_Data: {},
    createMemo_PropsToUpdate: {},
    createMemo_Saved: false,
    createMemo_IsSaving: false,
    isEdit: false,

    createMemo_CancelPrompt: false,
}



/**
* Memos module getters
* @const
*/
const getters = {
    /**
    * This getter returns the events attached to the currently loaded memo
    * @function
    * @param {state} state
    */
    getMemoEvents: state => {
        if (state.memoLoaded)
            return state.memo.events
        return []
    },


    /**
    * This getter returns the files attached to the currently loaded memo
    * @function
    * @param {state} state
    */
    getMemoAttachments: state => {
        if (state.memoLoaded)
            return state.memoAttachments
        return []
    },


    /**
    * This getter returns the team members attached to the currently loaded memo
    * @function
    * @param {state} state
    */
    getMemoTeam: state => {
        if (state.memoLoaded)
            return state.memo.team
        return []
    },


    /**
    * This getter returns the swaps attached to the currently loaded memo
    * @function
    * @param {state} state
    */
    getMemoSwapRows: state => {
        if (state.memoLoaded)
            return state.memo.swapRows
        return []
    },


    /**
    * This getter returns the swaps attached to the currently loaded memo
    * @function
    * @param {state} state
    */
    getMemoId: state => {
        if (state.memoLoaded)
            return state.memo.id
        return null
    },


    /**
    * This getter returns the swaps attached to the currently loaded memo
    * @function
    * @param {state} state
    */
    getCreateMemoData: state => {
        return state.createMemo_Data
    }
}




/**
 * Memos module action definitions
 * @const
 */
const actions = {
    /**
    * Get all memos
    * Params :
        approved: (approved memos | memo requests)
        ...table pagination (sorting, page, itemsPerRow, etc)
        filters
    * @function
    */
    /* TODO: needs error handling */
    [VUEX_MEMOS_GET_REQUEST]: async ({ state, commit }) => {
        commit(VUEX_MEMOS_GET_REQUEST)

        // Pagination looks like this 
        // {"rowsPerPage":8,"sortBy":"memo_type_id","page":1,"descending":true,"totalItems":0}
        // Convert it

        let params = Object.assign(
            {
                approved: state.memosApproved,
                page: state.pagination.page,
                per_page: state.pagination.rowsPerPage,
                sort: state.pagination.descending ? '-' + state.pagination.sortBy : state.pagination.sortBy
            },
        )

        for (var f in state.appliedFilters) {
            var key = state.appliedFilters[f]['filterOn']
            var val = state.appliedFilters[f]['id']
            if (!params.hasOwnProperty(key)) {
                params[key] = val
            } else {
                params[key] = params[key] + ',' + val
            }
        }

        await api.get('memos', { params }).then(async (response) => {
            await commit(VUEX_MEMOS_GET_SUCCESS, response.data)
        }).catch((err) => {
            commit(VUEX_MEMOS_GET_FAILURE, err)
        })  
    },

    [VUEX_MEMOS_CLEAR_FILTERS]: async ({ commit }) => {
        await commit(VUEX_MEMOS_CLEAR_FILTERS)
    },

    [VUEX_MEMOS_APPLY_FILTERS]: async ({ commit, dispatch }, filterItem) => {
        await commit(VUEX_MEMOS_APPLY_FILTERS, filterItem)
        await dispatch(VUEX_MEMOS_GET_REQUEST)
    },

    [VUEX_MEMOS_APPLY_STATUS_FILTER]: async ({ dispatch }, filterItem) => {
        let newFilterItem = Object.assign(filterItem)
        newFilterItem.filterOn = 'status'
        await dispatch(VUEX_MEMOS_APPLY_FILTERS, newFilterItem)
    },

    [VUEX_MEMOS_APPLY_PROGRESS_FILTER]: async ({ dispatch }, filterItem) => {
        let newFilterItem = Object.assign(filterItem)
        newFilterItem.filterOn = 'progress'
        await dispatch(VUEX_MEMOS_APPLY_FILTERS, newFilterItem)
    },

    [VUEX_MEMOS_APPLY_TYPE_FILTER]: async ({ dispatch }, filterItem) => {
        let newFilterItem = Object.assign(filterItem)
        newFilterItem.filterOn = 'type'
        await dispatch(VUEX_MEMOS_APPLY_FILTERS, newFilterItem)
    },

    [VUEX_MEMOS_APPLY_DEPARTMENT_FILTER]: async ({ dispatch }, filterItem) => {
        let newFilterItem = Object.assign(filterItem)
        newFilterItem.filterOn = 'department'
        await dispatch(VUEX_MEMOS_APPLY_FILTERS, newFilterItem)
    },

    [VUEX_MEMOS_APPLY_CREATIVE_ROUND_FILTER]: async ({ dispatch }, filterItem) => {
        let newFilterItem = Object.assign(filterItem)
        newFilterItem.filterOn = 'cretive_round'
        await dispatch(VUEX_MEMOS_APPLY_FILTERS, newFilterItem)
    },

    [VUEX_MEMOS_SYNC_PAGINATION]: async({commit, dispatch}, pagination) => {
        if (state.memosLoaded) {
            await commit(VUEX_MEMOS_SYNC_PAGINATION, pagination)
            await dispatch(VUEX_MEMOS_GET_REQUEST)
        }
    },

    [VUEX_MEMO_PAGINATION_EXIT]: async(dispatch) => {
        await dispatch(VUEX_MEMO_PAGINATION_EXIT)
    },





    /**
    * Get memo -> get events -> get files
    * Params :
        memo id
    * @function
    */
    /* TODO: needs error handling */
    [VUEX_MEMO_GET_REQUEST]: ({ commit, dispatch }, id) => {
        commit(VUEX_MEMO_GET_REQUEST)

        api.get(`memos/${id}`).then(async (response) => {
            await commit(VUEX_MEMO_GET_SUCCESS, response.data)
            dispatch(VUEX_MEMO_GET_ATTACHMENTS, id)
        })
    },

    /**
    * Update the current memo's properties
    * @function
    */
    [VUEX_MEMO_UPDATE_REQUEST]: async ({ state, dispatch, commit }, payload) => {
        
        await api.patch(`/memos/${state.memo.id}`, payload).then((response) => {
            commit(VUEX_MEMO_UPDATE_REQUEST_SUCCESS, response)
            dispatch(VUEX_MEMO_GET_REQUEST, state.memo.id)
            dispatch(VUEX_MEMOS_GET_REQUEST)
            commit(VUEX_MEMO_CREATE_CANCEL)

            //Show snackbar for success
            dispatch(VUEX_SNACKBAR_SHOW, {
                description_text: "Success! Your event has been saved.",
                button_text: "",
                timeout: 4000,
                button_dispatch: "",
                button_dispatch_payload: {},
            })
        }).catch((err) => {
            commit(VUEX_MEMO_UPDATE_REQUEST_FAILURE, err)
        })
    },

    /**
     * Update the current memo's name
     * @function
     */
    [VUEX_MEMO_UPDATE_PROP]: async ({dispatch}, params) => {
        await dispatch(VUEX_MEMO_UPDATE_REQUEST, params)
    },


    /**
     * Create Memo Request
     * @function
     */
    [VUEX_MEMO_CREATE_INIT]: ({ state, commit, dispatch }, opts) => {
        commit(VUEX_MEMO_CREATE_APPLY_TEMPLATE, opts)
        commit(VUEX_MEMO_CREATE_INIT)

        //GLOBAL ACTION PROMPT: Pass array of optional states to watch
        var stateChangeWatchers = [
            ['memos','createMemo_Data']
        ]
        dispatch('VUEX_GLOBAL_ACTION_PROMPT_SET_STATE_CHANGE_WATCHERS', { stateChangeWatchers: stateChangeWatchers})
    },

    [VUEX_MEMO_CREATE]: debounce(async ({ state, commit, dispatch }, payload) => {
        commit(VUEX_MEMO_CREATE)

        await api.post(`/memos`,payload).then((response) => {
            commit(VUEX_MEMO_CREATE_SUCCESS, response.data.data)
            commit(VUEX_MEMO_CREATE_CLEAR_PROPS_TO_UPDATE)
            dispatch(VUEX_MEMOS_GET_REQUEST)
            commit(VUEX_MEMO_CREATE_CANCEL)

            //Show snackbar for success
            dispatch(VUEX_SNACKBAR_SHOW, {
                description_text: "Success! Your memo has been created.",
                button_text: "",
                timeout: 4000,
                button_dispatch: "",
                button_dispatch_payload: {},
            })


        }).catch((err) => {
            commit(VUEX_MEMO_CREATE_FAILURE)
            console.error(err)
        })      

        // TODO: Move this to dispatch in routing
        // TODO: Diff destination based on role
        // router.push({ name: 'memos-manage' })
    }, 1200),

    [VUEX_MEMO_CREATE_UPDATE]: async ({ state, commit, dispatch }) => {
        // commit(VUEX_MEMO_CREATE_UPDATE)

        await api.patch(`/memos/${state.createMemo_Data.id}`, state.createMemo_Data).then((response) => {
            commit(VUEX_MEMO_CREATE_UPDATE_SUCCESS, response.data.data)
            dispatch(VUEX_MEMOS_GET_REQUEST)
            commit(VUEX_MEMO_CREATE_CLEAR_PROPS_TO_UPDATE)
        }).catch((err) => {
            commit(VUEX_MEMO_CREATE_UPDATE_FAILURE)
            console.error(err)
        })      
    },

    [VUEX_MEMO_CREATE_HAS_CHANGED]: ({state, commit}) => {
        if(!isEmpty(state.createMemo_PropsToUpdate)) {
            commit(VUEX_MEMO_CREATE_HAS_CHANGED, true)
        } else {
            commit(VUEX_MEMO_CREATE_HAS_CHANGED, false)
        }
    },

    [VUEX_MEMO_CREATE_CANCEL]: async ({ state, commit, dispatch }) => {
        await commit(VUEX_MEMO_CREATE_CANCEL_PROMPT, false)
        commit(VUEX_MEMO_CREATE_CANCEL)
    },


    [VUEX_MEMO_CREATE_UPDATE_PROP]: ({ commit, dispatch }, payload) => {
        commit(VUEX_MEMO_CREATE_UPDATE_PROP, payload)
    },

    [VUEX_MEMO_REMOVE]: async ({ state, commit, dispatch }) => {
        await commit(VUEX_MEMO_REMOVE)

        await api.delete(`/memos/${state.memo.id}`).then((response) => {
            commit(VUEX_MEMO_REMOVE_SUCCESS, response.data.data)
            dispatch(VUEX_MEMOS_GET_REQUEST)
            commit(VUEX_MEMO_CREATE_CANCEL)
        }).catch((err) => {
            commit(VUEX_MEMO_REMOVE_FAILURE)
            console.error(err)
        })  
    },



    /**
	 * File Attachment
	 */
	[VUEX_MEMO_ATTACH_FILE]: async ({ dispatch }, payload) => {
		api.post(`/attachments`, {
			file_id: payload.id,
			model_id: payload.attach_to.model_id,
			model: payload.attach_to.model,
			note: payload.note || null,
		}).then(async (response) => {
            // Do we need to get memo attachments immedeiatly after upload?
			// dispatch(VUEX_MEMO_GET_ATTACHMENTS, payload.attach_to.model_id)
		}).catch((err) => {
			console.error(err)
		})
    },
    
    [VUEX_MEMO_GET_ATTACHMENTS]: async ({ state, commit }, payload) => {
        let memo_id = payload ? payload : state.memo.id
       
        api.get(`/memos/${memo_id}/attachments`).then((response) => {
            commit(VUEX_MEMO_GET_ATTACHMENTS_SUCCESS, response.data.data)
        })
    },
    
    [VUEX_MEMO_REMOVE_FILE]: async ({ dispatch, commit }, payload) => {
		await dispatch(VUEX_ATTACHMENTS_DELETE_ATTACHMENT, payload).then((response) => {
			if (!response.status) return
			if (response.status == 200 || response.status == 204) 
				commit(VUEX_MEMO_REMOVE_FILE_SUCCESS, payload)
		})
	},
}

/**
 * Memos module mutation definitions
 * @const
 */
const mutations = {

    [VUEX_MEMOS_GET_REQUEST]: (state) => {
        state.memos = []
        state.memosLoaded = false
        state.pagination.totalItems = 0
    },

    [VUEX_MEMOS_GET_SUCCESS]: (state, payload) => {
        state.memos = payload.data
        state.memosLinks = payload.links
        state.memosLoaded = true
        state.pagination.totalItems = payload.meta.total
        state.pagination.page = payload.meta.current_page
    },

    [VUEX_MEMOS_GET_FAILURE]: () => {
        // 
    },

    //
    // Filtering
    //
    [VUEX_MEMOS_APPLY_FILTERS]: (state, filterItem) => {
        let idx = state.appliedFilters.findIndex(x => x.id === filterItem.id && x.filterOn === filterItem.filterOn)
        if (idx > -1) {
            state.appliedFilters.splice(idx, 1) // remove
        } else {
            state.appliedFilters.push(filterItem) // add
        }
    },

    [VUEX_MEMOS_CLEAR_FILTERS]: (state) => {
        state.appliedFilters = []
    },


    [VUEX_MEMOS_SYNC_PAGINATION]: (state, pagination) => {
        state.pagination = pagination
    },

    [VUEX_MEMOS_SET_APPROVED]: (state, value) => {
        state.memosApproved = value
    },



    
    [VUEX_MEMO_GET_REQUEST]: (state) => {
        state.memo = {}
        state.memoLoaded = false
    },

    [VUEX_MEMO_GET_SUCCESS]: (state, payload) => {
        state.memo = payload.data
        state.memoLinks = payload.links
        state.memoLoaded = true
        state.createMemo_Saved = true
        let match = state.memos.filter(item => item.id == payload.data.id)
        if(match.length) match[0].viewed_status = true
    },

    [VUEX_MEMO_GET_EVENTS_SUCCESS]: (state, payload) => {
        state.memoEvents = payload.data
        state.memoEventsLinks = payload.links
    },

    [VUEX_MEMO_UPDATE_APPLY_TEMPLATE]: (state) => {
        Object.assign(state.updateMemo_Data, updateMemo_Template)
    },

    [VUEX_MEMO_UPDATE_APPLY_TEMPLATE]: (state) => {
        state.updateMemo_Data = updateMemo_Template
    },


    [VUEX_MEMO_UPDATE_REQUEST_SUCCESS]: (rootState, payload) => {
        rootState.lastSuccess = payload
    },

    [VUEX_MEMO_UPDATE_REQUEST_FAILURE]: (rootState, error) => {
        rootState.lastError = error
    },

    [VUEX_MEMO_CREATE_APPLY_TEMPLATE]: (state, opts) => {
        state.createMemo_Data = cloneDeep(createMemo_Template)

        state.memoAttachments = []

        if (opts && opts.edit) {
            state.isEdit = true
        } else {
            state.isEdit = false
        }
    },

    [VUEX_MEMO_CREATE_INIT]: (state, payload) => {
        state.createMemo_CancelPromp = false
    },

    // CREATE MEMO
    [VUEX_MEMO_CREATE]: (state) => {
        state.createMemo_IsSaving = true
    },

    [VUEX_MEMO_CREATE_SUCCESS]: (state, payload) => {
        state.createMemo_Data = payload
        state.createMemo_IsSaving = false
        state.createMemo_Saved = true
    },

    [VUEX_MEMO_CREATE_FAILURE]: (state) => {
        state.createMemo_IsSaving = false
        state.createMemo_Saved = false
        // TODO: report error
    },


    // UPDATE MEMO
    [VUEX_MEMO_CREATE_UPDATE]: (state) => {
        state.createMemo_IsSaving = true
    },

    [VUEX_MEMO_CREATE_UPDATE_SUCCESS]: (state, payload) => {
        state.createMemo_Data = payload
        state.createMemo_IsSaving = false
    },

    [VUEX_MEMO_CREATE_UPDATE_FAILURE]: (state) => {
        state.createMemo_IsSaving = false
        state.createMemo_Saved = false
        // TODO: report error
    },

    [VUEX_MEMO_CREATE_CLEAR_PROPS_TO_UPDATE]: (state) => {
        var props = Object.getOwnPropertyNames(state.createMemo_PropsToUpdate);
        for (var i = 0; i < props.length; i++) {
          delete state.createMemo_PropsToUpdate[props[i]]
        }
    },


    //  UPDATE PROP IN STORE
    [VUEX_MEMO_CREATE_UPDATE_PROP]: (state, payload) => {
                // TODO: can this be done better/somewhere else
        // switch( Object.keys(payload)[0] ) {

        //     case 'name' :
        //         delete state.createMemo_PropsToUpdate.name
        //         break

        //     case 'content' :
        //         delete state.createMemo_PropsToUpdate.content
        //         break

        //     default :
        //         break
        // }

        let mergedData = Object.assign(state.createMemo_Data, payload)
        state.createMemo_Data = cloneDeep(mergedData)

        Object.assign(state.createMemo_PropsToUpdate, payload)
    },

    [VUEX_MEMO_CREATE_HAS_CHANGED]: (state, payload) => {
        state.createMemo_HasChanged = payload
    },

    // CANCEL MEMO
    [VUEX_MEMO_CREATE_CANCEL]: (state) => {
        state.createMemo_IsSaving = false
        //state.createMemo_Saved = false
    },

    [VUEX_MEMO_CREATE_CANCEL_PROMPT]: (state, payload) => {
        state.createMemo_CancelPrompt = payload
    },

    [VUEX_MEMO_REMOVE]: (state, payload) => {
    },


    [VUEX_MEMO_REMOVE_SUCCESS]: (state, payload) => {
        state.createMemo_Data = payload
        state.createMemo_IsSaving = false
    },

    [VUEX_MEMO_REMOVE_FAILURE]: (state, payload) => {
        state.createMemo_IsSaving = false
        state.createMemo_Saved = false
        // TODO: report error
    },


    //
    // AUTH EVENTS
    //
    [VUEX_AUTH_LOGOUT]: (state) => {
        state.memos = []
        state.memosLoaded = false
        state.memo = {}
        state.memoLoaded = false
    },


    //
    // ROUTING EVENTS
    //
    [VUEX_ROUTING_EXIT_MEMO]: (state) => {
        state.restorePreviousMemoList = true
    },

    [VUEX_MEMO_PAGINATION_EXIT]: (state) => {
        state.pagination.page = 1
        state.pagination.sortBy = 'created_at'
        state.pagination.descending = true
    },

    // MEMO ATTACHMENTS
    [VUEX_MEMO_GET_ATTACHMENTS_SUCCESS]: (state, payload) => {
        state.memoAttachments = payload
    },

    [VUEX_MEMO_REMOVE_FILE_SUCCESS]: (state, payload) => {
        let fileIndex = state.memoAttachments.findIndex(file => file.file_id === payload.file_id)
		if (fileIndex > -1) {
			state.memoAttachments.splice(fileIndex, 1)
		}
    }
}

/**
 * The Memos module of our Vuex store.
 * @module store/modules/memos
 */
export default {
  state,
  getters,
  actions,
  mutations,
}
